import React, { useEffect, useMemo, useState } from 'react'
import { Dialog, DialogActions, DialogContent, IconButton } from '@mui/material'
import { Paperclip, Search, X } from 'lucide-react'
import Button from '../../ui/Button'
import type { AppendixSummary } from '../../../types/codeIndex'

type ReferenceAppendixModalProps = {
  open: boolean
  // Describes the target, e.g. "Code item 02 · TYPE — applies to all 5 values".
  subtitle: string
  appendices: AppendixSummary[]
  // Appendices that cannot be picked, with the reason shown under them.
  disabledKeys?: ReadonlyMap<string, string>
  // Domain ids of the last appendices picked on this codesheet, newest first.
  recentKeys?: ReadonlyArray<string>
  onClose: () => void
  onReference: (key: string) => void
}

export const NO_DOMAIN_ID_HINT = 'Save this appendix once to make it referenceable'

export function formatAppendixName(appendix: Pick<AppendixSummary, 'letter' | 'title'>): string {
  const title = appendix.title || 'Untitled appendix'
  return appendix.letter ? `${appendix.letter} — ${title}` : title
}

export function formatAppendixSize(appendix: Pick<AppendixSummary, 'columnCount' | 'rowCount'>): string {
  const columns = `${appendix.columnCount} ${appendix.columnCount === 1 ? 'column' : 'columns'}`
  const rows = `${appendix.rowCount} ${appendix.rowCount === 1 ? 'row' : 'rows'}`
  return `${columns} · ${rows}`
}

function compareByLetter(a: AppendixSummary, b: AppendixSummary): number {
  if (a.letter.length !== b.letter.length) return a.letter.length - b.letter.length
  return a.letter < b.letter ? -1 : a.letter > b.letter ? 1 : 0
}

const NO_DISABLED: ReadonlyMap<string, string> = new Map()
const NO_RECENT: ReadonlyArray<string> = []

export const ReferenceAppendixModal: React.FC<ReferenceAppendixModalProps> = ({
  open,
  subtitle,
  appendices,
  disabledKeys = NO_DISABLED,
  recentKeys = NO_RECENT,
  onClose,
  onReference,
}) => {
  const [search, setSearch] = useState('')
  const [selectedKey, setSelectedKey] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setSearch('')
    setSelectedKey(null)
  }, [open])

  const sorted = useMemo(() => [...appendices].sort(compareByLetter), [appendices])
  const lastLetter = sorted.length > 0 ? sorted[sorted.length - 1].letter : ''

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return sorted
    return sorted.filter(appendix =>
      appendix.letter.toLowerCase() === query
      || appendix.letter.toLowerCase().startsWith(query)
      || appendix.title.toLowerCase().includes(query)
    )
  }, [search, sorted])

  const recent = useMemo(() => {
    const byKey = new Map(filtered.filter(appendix => appendix.key).map(appendix => [appendix.key as string, appendix]))
    return recentKeys
      .map(key => byKey.get(key))
      .filter((appendix): appendix is AppendixSummary => Boolean(appendix) && !disabledKeys.has(appendix!.key as string))
  }, [disabledKeys, filtered, recentKeys])

  const hintFor = (appendix: AppendixSummary): string | null => {
    if (!appendix.key) return NO_DOMAIN_ID_HINT
    return disabledKeys.get(appendix.key) ?? null
  }

  const renderOption = (appendix: AppendixSummary) => {
    const hint = hintFor(appendix)
    const disabled = hint !== null
    const checked = appendix.key !== null && selectedKey === appendix.key
    return (
      <label
        key={appendix.id}
        className={`flex cursor-pointer items-start gap-3 rounded-md px-3 py-2 ${checked ? 'bg-[#EEF6FF]' : 'hover:bg-[#F8FBFF]'} ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
      >
        <input
          type="radio"
          name="reference-appendix"
          className="mt-1 h-4 w-4 accent-[#1D5176]"
          value={appendix.key ?? ''}
          checked={checked}
          disabled={disabled}
          aria-label={formatAppendixName(appendix)}
          onChange={() => appendix.key && setSelectedKey(appendix.key)}
        />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-gray-900">{formatAppendixName(appendix)}</span>
          <span className="block text-xs text-gray-500">{hint ?? formatAppendixSize(appendix)}</span>
        </span>
      </label>
    )
  }

  const hasAppendices = appendices.length > 0
  const canReference = Boolean(selectedKey) && !disabledKeys.has(selectedKey as string)

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="reference-appendix-title"
      PaperProps={{ sx: { borderRadius: '8px' } }}
    >
      <div className="flex items-start justify-between border-b border-[#E2E8F0] bg-[#F3F9FF] px-5 py-4">
        <h2 id="reference-appendix-title" className="text-lg font-semibold text-gray-900">Reference an appendix</h2>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <X className="h-5 w-5 text-[#1D5176]" aria-hidden="true" />
        </IconButton>
      </div>

      <DialogContent sx={{ padding: '16px 20px 8px' }}>
        {hasAppendices ? (
          <div className="space-y-3">
            <p className="text-sm text-[#1D5176]">{subtitle}</p>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94A3B8]" aria-hidden="true" />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search by letter or name"
                aria-label="Search appendices"
                className="w-full rounded-md border border-[#CBD5E1] py-2 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#1D74D8] focus:outline-none"
              />
            </div>
            <p className="text-xs text-gray-500">
              {`${filtered.length} ${filtered.length === 1 ? 'appendix' : 'appendices'}`}{lastLetter ? ` · sorted A–${lastLetter}` : ''}
            </p>

            {filtered.length === 0 ? (
              <p className="py-4 text-center text-sm text-gray-500">No appendix matches your search.</p>
            ) : (
              <div className="max-h-[360px] space-y-3 overflow-y-auto pr-1">
                {recent.length > 0 ? (
                  <section aria-label="Recently referenced">
                    <h3 className="mb-1 px-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">Recently referenced</h3>
                    <div className="space-y-0.5">{recent.map(renderOption)}</div>
                  </section>
                ) : null}
                <section aria-label="All appendices">
                  <h3 className="mb-1 px-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">
                    {`All appendices${lastLetter ? ` (A–${lastLetter})` : ''}`}
                  </h3>
                  <div className="space-y-0.5">{filtered.map(renderOption)}</div>
                </section>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3 py-4 text-center">
            <div className="rounded-full bg-[#EEF6FF] p-3 text-[#1D5176]">
              <Paperclip className="h-5 w-5" aria-hidden="true" />
            </div>
            <p className="text-base text-gray-900">No appendices yet</p>
            <p className="text-sm text-gray-500">
              You can only reference an appendix that already exists. Create one in the Appendices panel first.
            </p>
          </div>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: '12px 20px 16px', borderTop: '1px solid #E2E8F0' }}>
        <Button type="button" variant="secondary" size="sm" className="bg-[#EEF6FF]" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          size="sm"
          disabled={!canReference}
          onClick={() => { if (selectedKey) onReference(selectedKey) }}
        >
          Reference
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ReferenceAppendixModal
